import { z } from "zod";
import { commentOutputSchema } from "./comments";

export type EmitToRoomOptions = {
    /**
     * The user IDs to exclude from the message.
     * This is useful when you want to send a message to all users in a room except for a few.
     * This exclusion is on the CLIENT and the data is sent anyway !!!
     */
    excludeUserIds?: string[];
};

// Common constants

export const socketEvent = {
    /** A new comment has been added */
    comment_new: "comment:new",
    /** Refetch the tasks in the task table */
    refetch_tasks: "refetch_tasks",
    /** Refetch the tasklists in the sidebar */
    refetch_tasklists: "refetch_tasklists",
    /** Refetch the inbox */
    refetch_inbox: "refetch_inbox",
    /** A new task was created and the recipient has access to it */
    task_created: "task:created",
    /** An existing task was edited; recipient should refetch only that task */
    task_updated: "task:updated",
    /** A task was deleted; recipient should drop it from any local cache */
    task_deleted: "task:deleted",
    /** The time entries of one (task, timer attribute) pair changed */
    timer_changed: "timer:changed",
    /** The recipient's own running timer started, moved to another entry, or stopped */
    timer_active_changed: "timer:active",
} as const;

// Schema's for various socket messages

export const refetchTasksMessageSchema = z.object({
    taskId: z.number(),
    taskListIds: z.array(z.number()),
});

export const taskCreatedMessageSchema = z.object({
    taskId: z.number(),
});

export const taskUpdatedMessageSchema = z.object({
    taskId: z.number(),
});

export const taskDeletedMessageSchema = z.object({
    taskId: z.number(),
});

export const refetchTasklistsMessageSchema = z.object({
    taskListId: z.number(),
});

export const refetchInboxMessageSchema = z.object({});

export const newCommentMessageSchema = z.object({
    comment: commentOutputSchema,
});

/**
 * Both timer payloads carry ids only. The client refetches, which keeps a running
 * `startedAt` (and the clock-skew handling it would need) off the wire, and avoids
 * leaking another organization's task title into a user room.
 */
export const timerChangedMessageSchema = z.object({
    taskId: z.number(),
    customAttributeId: z.number(),
});

export const timerActiveChangedMessageSchema = z.object({
    activeEntryId: z.number().nullable(),
    taskId: z.number().nullable(),
});
